import type { ReactNode } from 'react'
import { NavLink, useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/api/client'

/** Structura de navigatie e cea din TravelJoy: o lista plata, scurta, cu nume pe intelesul
 *  omului — nu pe structura bazei de date. Sub-sectiunile stau IN pagina (vezi SubNav),
 *  tot ca acolo (Templates, Reports, Settings). */
const NAV = [
  { to: '/tablou', prefix: '/tablou', label: 'Tablou' },
  { to: '/sarcini', prefix: '/sarcini', label: 'Sarcini' },
  { to: '/calendar', prefix: '/calendar', label: 'Calendar' },
  { to: '/contacte', prefix: '/contacte', label: 'Contacte' },
  { to: '/rezervari', prefix: '/rezervari', label: 'Rezervari' },
  { to: '/marketing', prefix: '/marketing', label: 'Marketing' },
  { to: '/bani/facturi', prefix: '/bani', label: 'Facturi si bani' },
  { to: '/rapoarte', prefix: '/rapoarte', label: 'Rapoarte' },
  { to: '/sabloane', prefix: '/sabloane', label: 'Sabloane' },
  { to: '/setari', prefix: '/setari', label: 'Setari' },
]

type Eu = { username: string; full_name: string | null; is_admin: boolean }

export function Shell({ children }: { children: ReactNode }) {
  const { pathname } = useLocation()
  const { data: eu } = useQuery({
    queryKey: ['eu'], queryFn: () => api.get<Eu>('/users/me'), staleTime: 10 * 60_000, retry: false,
  })
  return (
    <div className="min-h-full flex">
      <aside className="w-[220px] shrink-0 border-r border-line bg-surface flex flex-col">
        <div className="px-5 h-16 flex items-center border-b border-line">
          <span className="font-semibold tracking-tight text-[16px]">EventTour</span>
        </div>
        <nav className="p-3 flex flex-col gap-0.5">
          {NAV.map((m) => {
            const activ = pathname === m.prefix || pathname.startsWith(m.prefix + '/')
            return (
              <NavLink key={m.prefix} to={m.to}
                className={'px-3 h-9 flex items-center rounded-[var(--radius-control)] text-[14.5px] transition-colors ' +
                  (activ ? 'bg-accent-soft text-accent-ink font-medium' : 'text-ink-soft hover:bg-page')}>
                {m.label}
              </NavLink>
            )
          })}
        </nav>
        <div className="mt-auto p-3 border-t border-line">
          <div className="px-3 py-2">
            <p className="text-[13.5px] font-medium truncate">{eu?.full_name || eu?.username || '—'}</p>
            <p className="text-[12px] text-muted">{eu?.is_admin ? 'administrator' : 'operator'}</p>
          </div>
          <a href="/" className="px-3 h-9 flex items-center rounded-[var(--radius-control)] text-[13.5px] text-muted hover:bg-page">
            ← Aplicatia veche
          </a>
        </div>
      </aside>
      <main className="flex-1 min-w-0">{children}</main>
    </div>
  )
}

/** Sub-sectiunile fiecarei sectiuni, ca la TravelJoy: navigatia din stanga ramane plata,
 *  iar detaliul sectiunii se alege din taburile de sub titlu. */
const TABURI: { prefix: string; iteme: { to: string; label: string; doarAdmin?: boolean }[] }[] = [
  { prefix: '/bani', iteme: [
    { to: '/bani/facturi', label: 'Facturi' },
    { to: '/bani/incasari', label: 'Incasari' },
    { to: '/bani/plati', label: 'Plati' },
    { to: '/bani/avansuri', label: 'Avansuri' },
    { to: '/bani/casa', label: 'Casa' },
  ] },
  { prefix: '/rezervari', iteme: [
    { to: '/rezervari', label: 'Toate' },
    { to: '/rezervari/plecari', label: 'Plecari' },
    { to: '/rezervari/oferte', label: 'Oferte' },
    { to: '/rezervari/grupuri', label: 'Grupuri' },
  ] },
  { prefix: '/contacte', iteme: [
    { to: '/contacte', label: 'Toti' },
    { to: '/contacte/oportunitati', label: 'Oportunitati' },
  ] },
  { prefix: '/rapoarte', iteme: [
    { to: '/rapoarte', label: 'Vedere de ansamblu' },
    { to: '/rapoarte/vanzari', label: 'Vanzari si marja' },
    { to: '/rapoarte/clienti', label: 'Clienti si destinatii' },
    { to: '/rapoarte/furnizori', label: 'Furnizori' },
    { to: '/rapoarte/bani', label: 'Bani, TVA si avansuri' },
  ] },
  { prefix: '/sabloane', iteme: [
    { to: '/sabloane', label: 'Documente' },
    { to: '/sabloane/antet', label: 'Antet si identitate' },
    { to: '/sabloane/penalizari', label: 'Conditii de anulare' },
    { to: '/sabloane/mesaje', label: 'SMS si email' },
  ] },
  { prefix: '/setari', iteme: [
    { to: '/setari', label: 'Utilizatori', doarAdmin: true },
    { to: '/setari/tva', label: 'TVA' },
    { to: '/setari/observatii', label: 'Observatii client' },
    { to: '/setari/email', label: 'Email', doarAdmin: true },
    { to: '/setari/sincronizare', label: 'Sincronizare', doarAdmin: true },
    { to: '/setari/saga', label: 'SAGA', doarAdmin: true },
    { to: '/setari/joburi', label: 'Joburi', doarAdmin: true },
    { to: '/setari/import', label: 'Import fisiere' },
  ] },
]

export function PageHeader({
  title, count, actions,
}: { title: string; count?: string; actions?: ReactNode }) {
  const { pathname } = useLocation()
  const sectiune = TABURI.find((t) => pathname === t.prefix || pathname.startsWith(t.prefix + '/'))
  /** Acelasi `queryKey` ca in Shell, deci raspunsul e deja in cache — nu mai pleaca o cerere.
   *  Taburile de sistem (utilizatori, integrari, joburi) raspund 403 pentru conturile fara
   *  drept, deci nu se arata deloc, in loc sa duca intr-un ecran care refuza. */
  const { data: eu } = useQuery({
    queryKey: ['eu'], queryFn: () => api.get<Eu>('/users/me'), staleTime: 10 * 60_000, retry: false,
  })
  const iteme = sectiune?.iteme.filter((i) => !i.doarAdmin || eu?.is_admin)
  return (
    <header className="px-8 border-b border-line bg-surface">
      <div className="h-16 flex items-center justify-between">
        <div className="flex items-baseline gap-3">
          <h1 className="text-[19px] font-semibold tracking-tight">{title}</h1>
          {count && <span className="text-[13.5px] text-muted tnum">{count}</span>}
        </div>
        <div className="flex items-center gap-2">{actions}</div>
      </div>
      {iteme && iteme.length > 0 && <SubNav iteme={iteme} />}
    </header>
  )
}

/** Sub-sectiunile unei pagini, ca la TravelJoy (Reports -> Overview / Commissions / …). */
export function SubNav({ iteme }: { iteme: { to: string; label: string }[] }) {
  const { pathname } = useLocation()
  return (
    <div>
      <div className="flex flex-wrap gap-1 -mb-px">
        {iteme.map((i) => {
          const activ = i.to === pathname
            || (i.to.split('/').length > 2 && pathname.startsWith(i.to + '/'))
          return (
            <NavLink key={i.to} to={i.to}
              className={'px-3 h-9 flex items-center text-[14px] border-b-2 transition-colors ' +
                (activ ? 'border-accent text-ink font-medium' : 'border-transparent text-muted hover:text-ink')}>
              {i.label}
            </NavLink>
          )
        })}
      </div>
    </div>
  )
}
